import React, { useState } from 'react';
import {
  Search,
  CheckCircle2,
  Clock,
  AlertCircle,
  Eye,
  Send,
  RefreshCw,
  Layers,
  FileText,
  Edit3,
} from 'lucide-react';
import { WorkspaceUser, UserGroup, SavedSignatureTemplate } from '../types';
import { EditUserModal } from './EditUserModal';
import { DEFAULT_SAVED_TEMPLATES } from '../services/templateGroupService';

interface UserTableProps {
  users: WorkspaceUser[];
  selectedUserIds: Set<string>;
  activePreviewUser: WorkspaceUser | null;
  groups?: UserGroup[];
  savedTemplates?: SavedSignatureTemplate[];
  domain?: string;
  companyPhoneDefault?: string;
  companyWebsiteDefault?: string;
  companyAddressDefault?: string;
  onToggleSelectUser: (userId: string) => void;
  onToggleSelectAll: () => void;
  onSelectPreviewUser: (user: WorkspaceUser) => void;
  onTriggerBatchInjection: () => void;
  onRefreshUsers: () => void;
  onAssignGroup?: (userIds: string[], groupId: string) => void;
  onAssignSignature?: (userIds: string[], signatureId: string) => Promise<void> | void;
  onUserUpdated?: (user: WorkspaceUser) => void;
  isLoading: boolean;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  selectedUserIds,
  activePreviewUser,
  groups = [],
  savedTemplates = [],
  domain = 'cerebrolabs.io',
  companyPhoneDefault,
  companyWebsiteDefault,
  companyAddressDefault,
  onToggleSelectUser,
  onToggleSelectAll,
  onSelectPreviewUser,
  onTriggerBatchInjection,
  onRefreshUsers,
  onAssignGroup,
  onAssignSignature,
  onUserUpdated,
  isLoading,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'synced' | 'pending' | 'error'>('ALL');
  const [groupFilter, setGroupFilter] = useState<string>('ALL');
  const [isAssigning, setIsAssigning] = useState(false);
  const [editingUser, setEditingUser] = useState<WorkspaceUser | null>(null);

  const allTemplates = React.useMemo(() => {
    const map = new Map<string, SavedSignatureTemplate>();
    DEFAULT_SAVED_TEMPLATES.forEach((t) => map.set(t.id, t));
    savedTemplates.forEach((t) => map.set(t.id, t));
    return Array.from(map.values());
  }, [savedTemplates]);

  const [targetAssignment, setTargetAssignment] = useState<string>(() => {
    if (savedTemplates.length > 0) return `tpl:${savedTemplates[0].id}`;
    return 'tpl:tpl_default';
  });

  React.useEffect(() => {
    if (savedTemplates.length > 0 && targetAssignment === 'tpl:default') {
      setTargetAssignment(`tpl:${savedTemplates[0].id}`);
    }
  }, [savedTemplates, targetAssignment]);

  // Filter users by search term, status and group
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.primaryEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.jobTitle && user.jobTitle.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.department && user.department.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus =
      statusFilter === 'ALL' ? true : user.signatureStatus === statusFilter;

    const userGroupId = user.groupId || 'group_main';
    const matchesGroup =
      groupFilter === 'ALL' ? true : userGroupId === groupFilter;

    return matchesSearch && matchesStatus && matchesGroup;
  });

  const isAllSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((user) => selectedUserIds.has(user.id));

  const selectedCount = selectedUserIds.size;

  const handleBatchAssign = async () => {
    const userIds = Array.from(selectedUserIds);
    if (userIds.length === 0) return;
    setIsAssigning(true);
    try {
      if (targetAssignment.startsWith('tpl:')) {
        const tplId = targetAssignment.replace('tpl:', '');
        if (onAssignSignature) {
          await onAssignSignature(userIds, tplId);
        }
      } else if (targetAssignment.startsWith('grp:')) {
        const grpId = targetAssignment.replace('grp:', '');
        if (onAssignGroup) {
          onAssignGroup(userIds, grpId);
        }
      }
    } catch (e) {
      console.error('Błąd podczas masowego przypisywania:', e);
    } finally {
      setIsAssigning(false);
    }
  };

  const renderStatusBadge = (status?: string) => {
    switch (status) {
      case 'synced':
        return (
          <span className="badge badge-success" style={{ padding: '2px 8px', fontSize: 11 }}>
            <CheckCircle2 size={11} />
            Zsynchronizowano
          </span>
        );
      case 'pending':
        return (
          <span className="badge badge-warning" style={{ padding: '2px 8px', fontSize: 11 }}>
            <Clock size={11} />
            Oczekuje
          </span>
        );
      case 'error':
        return (
          <span className="badge badge-danger" style={{ padding: '2px 8px', fontSize: 11 }}>
            <AlertCircle size={11} />
            Błąd
          </span>
        );
      default:
        return (
          <span className="badge badge-neutral" style={{ padding: '2px 8px', fontSize: 11 }}>
            Brak stopki
          </span>
        );
    }
  };

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}>
      {/* Table Toolbar */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
        }}
      >
        {/* Left: Filters & Search */}
        <div className="table-toolbar-filters" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
          {/* Search Box */}
          <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 320 }}>
            <Search
              size={14}
              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}
            />
            <input
              type="text"
              placeholder="Filtruj pracownika, email, dział..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-control"
              style={{ paddingLeft: 30, paddingRight: 10, height: 32, fontSize: 12 }}
            />
          </div>

          {/* Group Filter */}
          {groups.length > 0 && (
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className="input-control"
              style={{ padding: '4px 10px', width: 'auto', height: 32, fontSize: 12, cursor: 'pointer' }}
              title="Filtruj wg grupy / działu"
            >
              <option value="ALL">Wszystkie grupy ({groups.length})</option>
              {groups.map((grp) => (
                <option key={grp.id} value={grp.id}>
                  {grp.name}
                </option>
              ))}
            </select>
          )}

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="input-control"
            style={{ padding: '4px 10px', width: 'auto', height: 32, fontSize: 12, cursor: 'pointer' }}
            title="Filtruj wg statusu stopki"
          >
            <option value="ALL">Wszystkie statusy</option>
            <option value="synced">✓ Zsynchronizowane</option>
            <option value="pending">⏳ Oczekujące</option>
            <option value="error">⚠ Błędy</option>
          </select>
        </div>

        {/* Right: Refresh */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={onRefreshUsers}
            disabled={isLoading}
            className="btn btn-secondary"
            style={{ height: 32, padding: '5px 12px', fontSize: 12 }}
            title="Odśwież listę z Google Workspace Directory"
          >
            <RefreshCw size={13} className={isLoading ? 'animate-spin' : ''} />
            <span>Odśwież</span>
          </button>
        </div>
      </div>

      {/* Batch Action Toolbar when items selected */}
      {selectedCount > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            background: 'linear-gradient(90deg, rgba(2, 132, 199, 0.15) 0%, rgba(15, 23, 42, 0.6) 100%)',
            border: '1px solid #0284c7',
            padding: '6px 12px',
            borderRadius: 8,
            animation: 'fadeIn 0.15s ease-out',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#0B192C' }}>
            <Layers size={14} color="#0066FF" />
            <span>Zaznaczono <strong>{selectedCount}</strong> z {users.length} pracowników</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <select
              value={targetAssignment}
              onChange={(e) => setTargetAssignment(e.target.value)}
              className="input-control"
              style={{ padding: '4px 8px', width: 'auto', fontSize: 11.5, height: 28, background: '#f8fafc' }}
            >
              <optgroup label="📋 Szablony podpisów (Firestore)">
                {allTemplates.map((tpl) => (
                  <option key={`tpl:${tpl.id}`} value={`tpl:${tpl.id}`}>
                    Szablon: {tpl.name}
                  </option>
                ))}
              </optgroup>
              {groups.length > 0 && (
                <optgroup label="👥 Działy / Grupy">
                  {groups.map((grp) => (
                    <option key={`grp:${grp.id}`} value={`grp:${grp.id}`}>
                      Grupa: {grp.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            <button
              type="button"
              disabled={isAssigning}
              onClick={handleBatchAssign}
              className="btn btn-secondary"
              style={{ padding: '4px 10px', fontSize: 11.5, height: 28, fontWeight: 600 }}
            >
              {isAssigning ? 'Zapisywanie...' : 'Zastosuj szablon'}
            </button>

            <button
              type="button"
              onClick={onTriggerBatchInjection}
              disabled={isLoading}
              className="btn btn-primary"
              style={{ padding: '4px 12px', fontSize: 11.5, height: 28, fontWeight: 700 }}
            >
              <Send size={12} />
              <span>Wstrzyknij ({selectedCount})</span>
            </button>
          </div>
        </div>
      )}

      {/* Table Container with Compact Density */}
      <div className="table-wrapper">
        <table className="table table-compact">
          <thead>
            <tr>
              <th style={{ width: 36, textAlign: 'center', padding: '8px' }}>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={onToggleSelectAll}
                  className="custom-checkbox"
                  aria-label="Zaznacz wszystkich użytkowników"
                />
              </th>
              <th>Pracownik & Kontakt</th>
              <th>Stanowisko & Dział</th>
              <th>Szablon & Grupa</th>
              <th style={{ width: 130 }}>Status</th>
              <th style={{ minWidth: 165, width: 165, textAlign: 'right' }}>Akcja</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '28px 16px', color: '#64748b' }}>
                  Nie znaleziono pracowników spełniających podane kryteria.
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => {
                const isSelected = selectedUserIds.has(user.id);
                const isPreviewActive = activePreviewUser?.id === user.id;

                return (
                  <tr
                    key={user.id}
                    className={`${isSelected ? 'selected' : ''} ${isPreviewActive ? 'preview-active' : ''}`}
                    style={{
                      borderLeft: isPreviewActive ? '3px solid #0284c7' : '3px solid transparent',
                      cursor: 'pointer',
                    }}
                    onClick={() => onSelectPreviewUser(user)}
                  >
                    {/* Checkbox */}
                    <td
                      style={{ textAlign: 'center', width: 36, padding: '6px 8px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleSelectUser(user.id);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleSelectUser(user.id)}
                        className="custom-checkbox"
                        aria-label={`Zaznacz ${user.fullName}`}
                      />
                    </td>

                    {/* Employee info & Contact */}
                    <td style={{ padding: '6px 10px' }}>
                      <div className="avatar-cell" style={{ gap: 8 }}>
                        {user.avatarUrl ? (
                          <img
                            src={user.avatarUrl}
                            alt={user.fullName}
                            className="avatar-img"
                            style={{ width: 30, height: 30 }}
                          />
                        ) : (
                          <div className="avatar-fallback" style={{ width: 30, height: 30, fontSize: 11 }}>
                            {user.fullName
                              .split(' ')
                              .map((p) => p[0])
                              .slice(0, 2)
                              .join('')
                              .toUpperCase()}
                          </div>
                        )}
                        <div style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>
                            <span style={{ fontWeight: 600, color: '#0B192C', fontSize: 12.5, lineHeight: 1.2, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                              {user.fullName}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingUser(user);
                              }}
                              className="btn btn-secondary"
                              style={{
                                padding: '2px 8px',
                                fontSize: 10.5,
                                height: 22,
                                borderRadius: 4,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 3,
                                flexShrink: 0,
                                background: 'rgba(0, 102, 255, 0.08)',
                                border: '1px solid rgba(0, 102, 255, 0.25)',
                                color: '#0066FF',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                              title="Edytuj dane pracownika (zdjęcie twarzy, bezpośredni telefon, link)"
                            >
                              <Edit3 size={11} />
                              <span>Edytuj</span>
                            </button>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#0066FF', marginTop: 2, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                            <span>{user.primaryEmail}</span>
                            {user.phone && (
                              <span style={{ color: '#64748b' }}>
                                • {user.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Role & Department */}
                    <td style={{ padding: '6px 10px', minWidth: 120 }}>
                      <div style={{ fontWeight: 600, color: '#0B192C', fontSize: 12, lineHeight: 1.2, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                        {user.jobTitle || 'Brak stanowiska'}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 1, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                        {user.department || 'CerebroLabs'}
                      </div>
                    </td>

                    {/* Assigned Template & Group */}
                    <td style={{ padding: '6px 10px', minWidth: 130 }}>
                      {(() => {
                        const userGrp = groups.find((g) => g.id === (user.groupId || 'group_main')) || groups[0];
                        const assignedId = user.assignedSignatureId || user.customTemplateId || userGrp?.savedTemplateId || 'tpl_default';
                        const tplMatch = allTemplates.find((t) => t.id === assignedId || t.templateId === assignedId)
                          || DEFAULT_SAVED_TEMPLATES.find((t) => t.id === assignedId || t.templateId === assignedId);
                        const assignedTplName = tplMatch?.name || (assignedId === 'default' || assignedId === 'tpl_default' ? 'Domyślny' : assignedId);

                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 3,
                                background: 'rgba(2, 132, 199, 0.12)',
                                border: '1px solid rgba(2, 132, 199, 0.3)',
                                padding: '1px 6px',
                                borderRadius: 4,
                                fontSize: 10.5,
                                fontWeight: 600,
                                color: '#0066FF',
                                whiteSpace: 'nowrap',
                              }}
                              title={`Przypisany szablon: ${assignedTplName}`}
                            >
                              <FileText size={10} />
                              <span>{assignedTplName}</span>
                            </span>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 3,
                                background: '#f1f5f9',
                                border: '1px solid #e2e8f0',
                                padding: '1px 5px',
                                borderRadius: 4,
                                fontSize: 10.5,
                                color: '#64748b',
                                whiteSpace: 'nowrap',
                              }}
                              title={`Grupa: ${userGrp?.name || 'Grupa Główna'}`}
                            >
                              <span style={{ width: 5, height: 5, borderRadius: '50%', background: userGrp?.color || '#0284c7' }} />
                              <span>{userGrp?.name || 'Grupa Główna'}</span>
                            </span>
                          </div>
                        );
                      })()}
                    </td>

                    {/* Status badge */}
                    <td style={{ padding: '6px 10px', width: 130 }}>
                      {renderStatusBadge(user.signatureStatus)}
                    </td>

                    {/* Actions */}
                    <td style={{ textAlign: 'right', padding: '6px 10px', minWidth: 165, width: 165, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingUser(user);
                          }}
                          className="btn btn-secondary"
                          style={{ padding: '4px 9px', fontSize: 11, height: 26, flexShrink: 0, whiteSpace: 'nowrap' }}
                          title="Edytuj profil pracownika (zdjęcie, bezpośredni telefon, link)"
                        >
                          <Edit3 size={12} />
                          <span>Edytuj</span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectPreviewUser(user);
                          }}
                          className={`btn ${isPreviewActive ? 'btn-primary' : 'btn-outline'}`}
                          style={{ padding: '4px 9px', fontSize: 11, height: 26, flexShrink: 0, whiteSpace: 'nowrap' }}
                          title="Wybierz do podglądu stopki"
                        >
                          <Eye size={12} />
                          <span>Podgląd</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer hint */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 11.5,
          color: '#64748b',
          paddingTop: 2,
        }}
      >
        <span>
          Zaznaczono <strong>{selectedCount}</strong> z <strong>{users.length}</strong> kont użytkowników
        </span>
        <span>Kliknij wiersz pracownika, aby zobaczyć podgląd na żywo stopki →</span>
      </div>

      {/* Employee Profile Edit Modal */}
      {editingUser && (
        <EditUserModal
          isOpen={Boolean(editingUser)}
          user={editingUser}
          domain={domain}
          companyPhoneDefault={companyPhoneDefault}
          companyWebsiteDefault={companyWebsiteDefault}
          companyAddressDefault={companyAddressDefault}
          onClose={() => setEditingUser(null)}
          onSave={(updatedUser) => {
            if (onUserUpdated) {
              onUserUpdated(updatedUser);
            }
            setEditingUser(null);
          }}
        />
      )}
    </div>
  );
};
